import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { UserCircle2, KeyRound, Loader2, ShieldCheck, LogOut, Heart } from "lucide-react";

export default function Cuenta() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  const policyOk = (pwd) => pwd.length >= 8 && /[A-Za-zÁÉÍÓÚáéíóúÑñ]/.test(pwd) && /\d/.test(pwd);

  const submit = async (e) => {
    e.preventDefault();
    if (!policyOk(newPassword)) {
      toast.error("La nueva contraseña debe tener al menos 8 caracteres e incluir letras y números");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Las contraseñas nuevas no coinciden");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      if (res.data?.token) {
        localStorage.setItem("cfe_token", res.data.token);
      }
      toast.success(res.data?.detail || "Contraseña actualizada correctamente");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cambiar la contraseña");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (loading || !user) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 py-12">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="mt-6 h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-10 sm:px-6 sm:py-14" data-testid="cuenta-page">
      <div className="mb-8 flex items-center gap-2">
        <UserCircle2 className="h-6 w-6 text-primary" />
        <h1 className="font-serif text-3xl font-semibold">Mi cuenta</h1>
      </div>

      {/* Datos del usuario */}
      <Card className="border-border bg-card shadow-[var(--shadow-sm)]">
        <CardContent className="p-6">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="truncate font-serif text-xl font-semibold" data-testid="cuenta-nombre">{user.name}</p>
              <p className="truncate text-sm text-muted-foreground" data-testid="cuenta-email">{user.email}</p>
              {user.is_admin && (
                <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary">
                  <ShieldCheck className="h-3.5 w-3.5" /> Administrador
                </p>
              )}
            </div>
            <div className="flex shrink-0 flex-col gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate("/favoritos")} data-testid="cuenta-favoritos-link">
                <Heart className="mr-1.5 h-4 w-4" /> Favoritos
              </Button>
              <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive/10" onClick={handleLogout} data-testid="cuenta-logout-button">
                <LogOut className="mr-1.5 h-4 w-4" /> Salir
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cambio de contraseña */}
      <Card className="mt-6 border-border bg-card shadow-[var(--shadow-sm)]">
        <CardContent className="p-6">
          <div className="mb-1 flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" />
            <h2 className="font-serif text-xl font-semibold">Cambiar contraseña</h2>
          </div>
          <p className="mb-6 text-sm text-muted-foreground">
            Por tu seguridad, al cambiar la contraseña se cerrarán tus demás sesiones activas.
          </p>
          <form onSubmit={submit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="current-password">Contraseña actual</Label>
              <Input
                id="current-password"
                type="password"
                required
                maxLength={128}
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                data-testid="cuenta-current-password"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-password">Nueva contraseña</Label>
              <Input
                id="new-password"
                type="password"
                required
                maxLength={128}
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="cuenta-new-password"
              />
              <p className="text-xs text-muted-foreground">Mínimo 8 caracteres, con letras y números.</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="confirm-password">Confirmar nueva contraseña</Label>
              <Input
                id="confirm-password"
                type="password"
                required
                maxLength={128}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="cuenta-confirm-password"
              />
            </div>
            <Button type="submit" disabled={saving} className="w-fit" data-testid="cuenta-change-password-submit">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
              {saving ? "Actualizando…" : "Actualizar contraseña"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
